/**
 * Paas mental-effort prompt — 1-9 scale overlay.
 *
 * Sent on the next step submission as `paasEffort` (per spec §7.4).
 * Visual chassis intentionally similar to ConfidenceCheck so users learn
 * the pattern: a small overlay with a row of buttons and one-tap submission.
 *
 * See docs/fatigue-score/README.md §7.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useReadinessEvent } from '@/shared/api/hooks/useReadinessEvent';

interface PaasPromptProps {
  show: boolean;
  onSelect: (effort: number) => void;
  /** Telemetry context — used to fire readiness_paas_prompt_shown. */
  sessionId?: string;
  stepPosition?: number;
  algorithmVersion?: string;
}

const VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export function PaasPrompt({
  show,
  onSelect,
  sessionId,
  stepPosition,
  algorithmVersion,
}: PaasPromptProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const trackEvent = useReadinessEvent();

  // Fire readiness_paas_prompt_shown once per show transition.
  useEffect(() => {
    if (show) {
      trackEvent('readiness_paas_prompt_shown', {
        sessionId,
        stepPosition,
        algorithmVersion,
      });
    }
  }, [show, sessionId, stepPosition, algorithmVersion, trackEvent]);

  const handleSelect = useCallback(
    (n: number) => {
      if (selected !== null) return;
      setSelected(n);
      // Hold the visual confirmation briefly then commit.
      setTimeout(() => onSelect(n), 250);
    },
    [selected, onSelect]
  );

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.18 }}
          className="rounded-lg p-4"
          style={{
            background: 'var(--f-bg-elevated, var(--f-bg))',
            border: '1px solid var(--f-border, rgba(0,0,0,0.1))',
          }}
          role="dialog"
          aria-label="How much mental effort did that take?"
        >
          <div className="mb-3 text-sm" style={{ color: 'var(--f-fg)' }}>
            How much mental effort did that take?
          </div>
          <div className="flex flex-row gap-1 justify-center">
            {VALUES.map((n) => {
              const isSelected = selected === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => handleSelect(n)}
                  disabled={selected !== null}
                  className="flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2"
                  style={{
                    background: isSelected ? 'var(--f-accent, #6366f1)' : 'var(--f-bg)',
                    color: isSelected ? '#fff' : 'var(--f-fg)',
                    border: '1px solid var(--f-border, rgba(0,0,0,0.1))',
                    opacity: selected !== null && !isSelected ? 0.4 : 1,
                  }}
                  aria-label={`Effort level ${n} of 9`}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div
            className="mt-2 flex flex-row justify-between text-xs opacity-60"
            style={{ color: 'var(--f-fg)' }}
          >
            <span>Very, very low</span>
            <span>Very, very high</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
